import { Component, effect, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { FormFieldComponent } from 'src/app/shared/form-field/components/form-field/form-field.component';
import { FormFieldsDirective } from 'src/app/shared/form-field/directives/form-fields/form-fields.directive';
import { FormFieldService } from 'src/app/shared/form-field/services/form-field/form-field.service';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';
import { FormFieldOptions } from 'src/app/shared/form-field/form-field.types';
import { CryptotokenKey } from 'src/app/shared/entities/cryptotoken-key';
import { GenerateKeyRequestDto } from 'src/app/shared/services/api/dto/generate-key-request-dto';
import { CreateCryptoTokenKeyField, cryptoTokenKeyFormValueToDto } from '../create-crypto-token-key/create-crypto-token-key.types';
import { LangService } from 'src/app/shared/services/lang/lang.service';

@Component({
  selector: 'app-copy-crypto-token-key',
  imports: [
    MatButtonModule,
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldsDirective,
  ],
  providers: [FormFieldService],
  templateUrl: './copy-crypto-token-key.html',
  styleUrl: './copy-crypto-token-key.scss',
})
export class CopyCryptoTokenKeyComponent {
  CreateCryptoTokenKeyField = CreateCryptoTokenKeyField;
  private fb = inject(FormBuilder);
  private formFieldService = inject(FormFieldService);
  private lang = inject(LangService).lang;
  private labels = this.lang.features.cryptotokenlist.details.cryptoTokenKeys;
  value = input<CryptotokenKey>();
  aliasSuffix = input('');
  copiedValue = output<GenerateKeyRequestDto>();
  copyCanceled = output<void>();
  // Labels can be made configurable if need be
  title = this.labels.actions.create.copyTitle;
  okText = this.lang.shared.okText;
  cancelText = this.lang.shared.cancelText;

  form = this.fb.group({
    [CreateCryptoTokenKeyField.alias]: [
      '',
      [
        Validators.required,
        Validators.pattern(ValidationConstants.cryptotoken.namePattern),
      ],
    ],
    [CreateCryptoTokenKeyField.keySpecification]: ['', [Validators.required]],
  });

  onSubmit() {
    this.form.markAsDirty();
    if (this.form.valid) {
      const dto = cryptoTokenKeyFormValueToDto(this.form.value);
      this.copiedValue.emit(dto);
    }
  }

  onCancel() {
    this.copyCanceled.emit();
  }

  private setValueEffect = () => {
    const value = this.value();
    const aliasSuffix = this.aliasSuffix();
    const alias = value?.details.alias
      ? `${value.details.alias}${aliasSuffix}`
      : null;
    const keyAlgorithm = value?.details.keyAlgorithm ?? null;
    const keySpecification = value?.details.keySpecification ?? null;
    const keySpec = `${keyAlgorithm}${keySpecification}`;
    this.form.setValue({
      [CreateCryptoTokenKeyField.alias]: alias,
      [CreateCryptoTokenKeyField.keySpecification]: keySpec,
    });
  };

  constructor() {
    effect(this.setValueEffect);
    this.formFieldService.configure({
      labels: this.labels.actions.create.inputs,
      options: {
        [CreateCryptoTokenKeyField.alias]: FormFieldOptions.text({
          required: true,
        }),
        [CreateCryptoTokenKeyField.keySpecification]: FormFieldOptions.select({
          required: true,
        }),
      },
    });
  }
}
